/*
  base-classes.js — Restova shared BASE CLASSES (clean-room, de-vendor project)

  WHY THIS EXISTS
  The de-vendor swap deletes the 99KB minified legacy bundle that owned four
  foundational custom-element classes. Three of them are still EXTENDED by
  surviving local files via a bare `extends <Name>` (no import, no window.* read):

    - assets/product-modal.js   -> class ProductModal  extends ModalDialog
    - assets/quick-add.js       -> class QuickAddModal extends ModalDialog
    - assets/product-model.js   -> class ProductModel  extends DeferredMedia
    - assets/share.js           -> class ShareButton   extends DetailsDisclosure
    - assets/header-nav.js      -> class HeaderMenu     extends DetailsDisclosure

  For `extends ModalDialog` to resolve at class-evaluation time, `ModalDialog`
  must be a GLOBAL LEXICAL BINDING. That is why the four classes below are
  TOP-LEVEL `class` declarations in a CLASSIC (non-module) script: a top-level
  class declaration in a classic script creates a binding reachable by bare
  identifier from every other classic script. We ALSO assign `window.X = X`
  belt-and-suspenders, but the top-level declaration is the real contract.

  LOAD CONTRACT (load-bearing)
  - CLASSIC script, NOT an ES module. No import/export. No top-level IIFE wrapper
    around the class declarations (a wrapper would scope the names and break every
    bare `extends`).
  - `defer` in the <head>, ABOVE the body-emitted subclass files
    (product-modal.js / quick-add.js / product-model.js / share.js / header-nav.js)
    AND after theme-globals.js (which supplies trapFocus/removeTrapFocus/debounce)
    and after main.js (which supplies window.pauseAllMedia). Defer scripts run in
    document order and head-defer runs before body-defer, so this is guaranteed to
    evaluate before any subclass file and before any consumer connectedCallback.

  EXTERNAL GLOBALS USED (provided elsewhere, never redefined here):
  - trapFocus(container, elementToFocus), removeTrapFocus(elementToFocus)  -> theme-globals.js
  - window.pauseAllMedia()                                                 -> main.js

  DEFINE SAFETY
  Every customElements.define is guarded with `if (!customElements.get(tag))`.
  The legacy bundle defined these UNGUARDED — never replicate that; a second
  unguarded define throws and kills the rest of the evaluating script.

  Zero copied bytes from the legacy bundle. Behavior re-derived from the verified
  spec only.
*/

/* ---------------------------------------------------------------------------
   ModalDialog  (tag: modal-dialog)
   Generic accessible modal. Base class for ProductModal + QuickAddModal.

   Contract preserved EXACTLY:
     - close button matched by `[id^="ModalClose-"]`; the click handler is bound
       with a hard `false` first arg (hide(false)) — load-bearing because
       QuickAddModal.hide(preventFocus = false) reads that argument.
     - one-time self-relocation to <body> guarded by `this.moved` (prevents the
       appendChild -> connectedCallback infinite loop).
     - the `media-modal` class switches the outside-click flavor (pointerup/mouse,
       ignoring taps on deferred-media/product-model) vs (click on backdrop self).
     - show(opener) sets `this.openedBy` BEFORE returning (ProductModal.show reads
       this.openedBy immediately after super.show).
     - body scroll lock via the `overflow-hidden` body class (legacy lock; base.css
       forces body height:100vh — accepted, do not "upgrade" here).
     - dispatches `modalClosed` on document.body in hide().
--------------------------------------------------------------------------- */
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    // Close (X) button. The spec preserves the no-guard markup contract, but a
    // null-guard is safe: every modal in this theme renders a ModalClose-*
    // descendant, and guarding only avoids a constructor throw on malformed markup.
    const closeButton = this.querySelector('[id^="ModalClose-"]');
    if (closeButton) {
      // hide.bind(this, false) — the bound `false` is the subclass preventFocus arg.
      closeButton.addEventListener('click', this.hide.bind(this, false));
    }

    // Escape closes the dialog.
    this.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    if (this.classList.contains('media-modal')) {
      // Media modals: dismiss on a real MOUSE pointerup outside the media, but
      // never when the tap/click landed on a playing video/model (so interacting
      // with the media doesn't close the modal).
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'mouse') return;
        if (event.target.closest('deferred-media, product-model')) return;
        this.hide();
      });
    } else {
      // Plain modals: close only when the backdrop element itself is clicked.
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    // One-time relocation to the end of <body> so the modal escapes any overflow /
    // transform / stacking-context ancestor. The `moved` flag stops the appendChild
    // from re-triggering connectedCallback forever.
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;

    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup && typeof popup.loadContent === 'function') popup.loadContent();

    if (typeof trapFocus === 'function') {
      trapFocus(this, this.querySelector('[role="dialog"]'));
    }
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
  }
}

/* ---------------------------------------------------------------------------
   ModalOpener  (tag: modal-opener)
   Thin trigger. Resolves the dialog by `data-modal` (a CSS id selector) at CLICK
   time and calls dialog.show(button).

   The `:not(.internal-video__play, .internal-video__sound-btn)` button exclusion
   is load-bearing: an autoplay video poster can nest an <internal-video> (with
   play/sound buttons) inside the opener BEFORE the real trigger button — the
   exclusion makes us pick the real trigger.
--------------------------------------------------------------------------- */
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector(
      'button:not(.internal-video__play, .internal-video__sound-btn)'
    );
    if (!button) return; // silent no-op when there is no eligible trigger

    button.addEventListener('click', () => {
      // Re-read data-modal at click time: ModalDialog relocates itself to <body>
      // on connect, so an id selector (not an element ref) is required.
      const dialog = document.querySelector(this.getAttribute('data-modal'));
      if (dialog && typeof dialog.show === 'function') dialog.show(button);
    });
  }
}

/* ---------------------------------------------------------------------------
   DeferredMedia  (tag: deferred-media)
   Lazy-loads heavy media (<video>/<iframe>/<model-viewer>) from a <template> on
   first poster click (or on an external loadContent() call). Base class for
   ProductModel.

   Contract preserved EXACTLY:
     - poster matched by `[id^="Deferred-Poster-"]`; ABSENT poster => constructor
       early-returns and loadContent must be driven externally (the poster-less
       `.template-popup` collage variant). This early return is load-bearing.
     - load-once gate is the `loaded` ATTRIBUTE.
     - the `focus` param doubles as the click handler's event (a click passes a
       truthy MouseEvent => the inserted media is focused).
--------------------------------------------------------------------------- */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return; // externally-driven variant has no poster button
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

    if (this.getAttribute('loaded')) return; // load once

    const template = this.querySelector('template');
    if (!template || !template.content.firstElementChild) return;

    const temp = document.createElement('div');
    temp.appendChild(template.content.firstElementChild.cloneNode(true));
    this.setAttribute('loaded', true);

    const media = temp.querySelector('video, model-viewer, iframe');
    if (!media) return;

    // Move the cloned media node out of the temp wrapper into this element.
    const inserted = this.appendChild(media);
    if (focus && typeof inserted.focus === 'function') inserted.focus();
  }
}

/* ---------------------------------------------------------------------------
   DetailsDisclosure  (tag: details-disclosure)
   Accessible <details>/<summary> disclosure base. Closes when focus leaves the
   component; plays/cancels the content panel's CSS-transition Animation objects on
   toggle. Base class for HeaderMenu (header-nav.js) and ShareButton (share.js).

   DORMANT as a tag (no markup emits <details-disclosure>), but it MUST be defined:
   base.css L2808 styles `details-disclosure>details`, and cart-notification.js L78
   does `closest('details-disclosure, header-menu')`.

   Public surface relied on by subclasses:
     - this.mainDetailsToggle, this.content  (read by ShareButton/HeaderMenu)
     - close(), onToggle(), onFocusOut()     (HeaderMenu overrides onToggle;
                                              ShareButton binds close)
--------------------------------------------------------------------------- */
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return; // malformed markup: nothing to wire

    const summary = this.mainDetailsToggle.querySelector('summary');
    this.content = summary ? summary.nextElementSibling : null;

    // Keep bound refs so disconnectedCallback can detach them cleanly (the legacy
    // version leaked these; sections re-render in the theme editor).
    this._onFocusOut = this.onFocusOut.bind(this);
    this._onToggle = this.onToggle.bind(this);

    this.mainDetailsToggle.addEventListener('focusout', this._onFocusOut);
    this.mainDetailsToggle.addEventListener('toggle', this._onToggle);
  }

  disconnectedCallback() {
    if (!this.mainDetailsToggle) return;
    this.mainDetailsToggle.removeEventListener('focusout', this._onFocusOut);
    this.mainDetailsToggle.removeEventListener('toggle', this._onToggle);
  }

  onFocusOut() {
    // Defer one tick so the newly-focused node settles, then close if focus has
    // left the whole component.
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.content) return;
    // Lazy-cache the content panel's Animation objects on first toggle.
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    if (this.mainDetailsToggle) this.mainDetailsToggle.removeAttribute('open');
    const summary = this.mainDetailsToggle
      ? this.mainDetailsToggle.querySelector('summary')
      : null;
    if (summary) summary.setAttribute('aria-expanded', false);
  }
}

/* ---------------------------------------------------------------------------
   Belt-and-suspenders global property assignment (the top-level class
   declarations above are already the real global bindings the subclasses
   resolve; these mirror them onto window too).
--------------------------------------------------------------------------- */
window.ModalDialog = ModalDialog;
window.ModalOpener = ModalOpener;
window.DeferredMedia = DeferredMedia;
window.DetailsDisclosure = DetailsDisclosure;

/* Guarded custom-element registration (NEVER unguarded — a double define throws). */
if (!customElements.get('modal-dialog')) customElements.define('modal-dialog', ModalDialog);
if (!customElements.get('modal-opener')) customElements.define('modal-opener', ModalOpener);
if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);
if (!customElements.get('details-disclosure'))
  customElements.define('details-disclosure', DetailsDisclosure);
